import { ChevronDownIcon } from '@heroicons/react/16/solid';
import { ArchiveBoxIcon } from '@heroicons/react/20/solid';
import React, { useEffect, useMemo, useState } from 'react';
import { useMatch, useSearchParams } from 'react-router-dom';

import { motion } from 'framer-motion';
import { useProducts } from 'src/features/product/product-list/hooks/useProducts';
import { ROUTE_PATHS } from 'src/shared/config/routes';

import { cn } from 'src/shared/components/ui';

import { ButtonV2 } from '../../ui';
import SidebarItem from './SidebarItem';

interface ProductsListNavProps {
  isOpen: boolean;
}

const getPath = (id: string, detailTab: string | null) => {
  if (detailTab) {
    return `${ROUTE_PATHS.PRODUCTS}/${id}?detailTab=${detailTab}`;
  }

  return `${ROUTE_PATHS.PRODUCTS}/${id}`;
};

const ProductsListNav: React.FC<ProductsListNavProps> = ({ isOpen }) => {
  const [isVisible, setIsVisible] = useState(false);
  const { data } = useProducts();
  const [searchParams] = useSearchParams();

  const detailTab = searchParams.get('detailTab');

  const match = useMatch(ROUTE_PATHS.PRODUCT_DETAIL);

  const notArchivedProducts = useMemo(() => {
    return data?.filter((product) => !product.archived);
  }, [data]);

  useEffect(() => {
    if (!!match) {
      setIsVisible(true);
    }
  }, [match]);

  useEffect(() => {
    if (!isOpen) {
      if (isVisible) {
        setIsVisible(false);
      }
    }
  }, [isOpen, isVisible]);

  const [isScrollable, setIsScrollable] = useState(false);
  const [atBottom, setAtBottom] = useState(false);
  const scrollRef = React.useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = scrollRef.current;

    if (!el) return;

    const checkScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = el;

      setIsScrollable(scrollHeight > clientHeight);
      setAtBottom(scrollTop + clientHeight >= scrollHeight - 1);
    };

    checkScroll(); // initial check

    el.addEventListener('scroll', checkScroll);
    window.addEventListener('resize', checkScroll);

    return () => {
      el.removeEventListener('scroll', checkScroll);
      window.removeEventListener('resize', checkScroll);
    };
  }, [notArchivedProducts]);

  const renderProducts = () => {
    if (!notArchivedProducts) return;

    return (
      <div className="flex flex-col gap-[2px]">
        {notArchivedProducts?.map((product) => {
          const isLongTitle = product?.detail?.name?.length > 20;
          const truncateTitle = isLongTitle
            ? product.detail.name.slice(0, 20) + '...'
            : product.detail.name;

          return (
            <SidebarItem
              isProduct
              isLongTitle={isLongTitle}
              key={product.id}
              isOpen={isOpen}
              title={truncateTitle}
              tooltipContent={product.detail.name}
              path={getPath(product.id, detailTab)}
              icon={<span className="ml-1">•</span>}
            />
          );
        })}
      </div>
    );
  };

  return (
    <div className="relative flex flex-col gap-[2px] mb-[6px]">
      <SidebarItem
        isOpen={isOpen}
        title="Products"
        path={ROUTE_PATHS.PRODUCTS}
        icon={<ArchiveBoxIcon className="size-4" />}
      />
      {isOpen && (
        <ButtonV2
          onPress={() => setIsVisible((prev) => !prev)}
          isIconOnly
          size="xs"
          className="absolute right-3 top-[10px] bottom-0 bg-transparent"
        >
          <ChevronDownIcon
            className={cn(
              'size-4 text-secondary-text transition-all duration-200',
              isVisible ? 'rotate-180' : ''
            )}
          />
        </ButtonV2>
      )}

      <motion.div
        initial={
          isVisible
            ? { maxHeight: 460, opacity: 1 }
            : { maxHeight: 0, opacity: 0 }
        }
        animate={{
          maxHeight: isVisible ? 460 : 0,
          opacity: isVisible ? 1 : 0
        }}
        className={cn('overflow-hidden')}
      >
        <div
          ref={scrollRef}
          className="overflow-y-scroll products-list-scrollbar h-full max-h-[420px]"
        >
          {renderProducts()}
          {!atBottom && isScrollable && (
            <div
              id="bottomOverlay"
              className="absolute bottom-0 left-0 right-0 h-[80px] z-10 pointer-events-none"
              style={{
                background:
                  'linear-gradient(180deg, rgba(255, 255, 255, 0) 0%, #FFFFFF 100%)'
              }}
            />
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default ProductsListNav;
